import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaThLarge, FaShareAlt, FaTrash, FaEllipsisV, FaCheckSquare, FaSquare, FaLaptop, FaGlobe, FaCloudUploadAlt } from 'react-icons/fa';
import { isRunTelemetryCachedOnDevice } from '../utils/runDeviceCache';
import './RunCard.css';

const extractYouTubeVideoID = (url) => {
  const regExp = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/ ]{11})/;
  const match = url.match(regExp);
  return match ? match[1] : "";
};

/** Pending / offline queue (local draft or inline queue): not yet a normal Firestore card. */
function isRunPendingOnDevice(run) {
  if (run?.pendingSync) return true;
  const id = run?.id;
  if (typeof id !== 'string') return false;
  return id.startsWith('local-') || id.startsWith('queued-inline-');
}

/** Saved run document in the cloud (Firestore), not the device-only pending queue. */
function isRunCloudOnly(run) {
  if (isRunPendingOnDevice(run)) return false;
  const id = run?.id;
  return typeof id === 'string' && id.length > 0;
}

// Purple accent tints for placeholders (matches brand, varied by run ID)
const getAccentPlaceholderTint = (runId) => {
  if (!runId) return '#5a4d9e';

  let hash = 0;
  for (let i = 0; i < runId.length; i++) {
    const char = runId.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }

  const variations = [
    '#6a5cb8',
    '#5a4d9e',
    '#7d6fc9',
    '#4a3f85',
    '#8b7fd4',
    '#554a94',
    '#9b8ad6',
    '#453d7a',
    '#6e5fb8',
    '#5246a0',
    '#3d3568',
    '#8574c4',
    '#6449a6',
    '#7660b0',
    '#4f4490',
    '#a094e8',
    '#584b98',
    '#6958a8',
    '#4a4088',
  ];

  return variations[Math.abs(hash) % variations.length];
};

const RunCard = ({
  run,
  viewMode = 'grid', // grid | list
  onClick,
  onDelete,
  onShare,
  onOpenGroups,
  showSelect,
  selectMode = false,
  selected = false,
  onToggleSelect,
  getGradientForRun, // Optional, for homepage
  isMemberRun = false, // True when viewing a group member's run as manager
  /** Signed-in user uid — used to read per-device “telemetry opened here” flags for cloud runs. */
  viewerUserId,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [cacheTick, setCacheTick] = useState(0);
  const menuRef = useRef(null);
  const isDraggingRef = useRef(false);

  useEffect(() => {
    function onDocClick(e) {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target)) setMenuOpen(false);
    }
    if (menuOpen) document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [menuOpen]);

  useEffect(() => {
    const onMarked = (e) => {
      if (e.detail?.runId === run?.id && e.detail?.userId === viewerUserId) {
        setCacheTick((t) => t + 1);
      }
    };
    window.addEventListener('shokiq-run-device-cache', onMarked);
    return () => window.removeEventListener('shokiq-run-device-cache', onMarked);
  }, [run?.id, viewerUserId]);
  // Support both youtubeUrl and videoUrl, and check for videos array
  const videoUrl = run.youtubeUrl || run.videoUrl || (run.videos && run.videos.length > 0 ? run.videos[0].url : null);
  const youtubeId = videoUrl ? extractYouTubeVideoID(videoUrl) : null;
  const hasThumbnail = !!youtubeId;

  // Get run name
  const runName = run.runName || run.name || 'Unnamed Run';
  
  // Get date - handle Firebase timestamps and various date formats. Never return an object (e.g. Firestore Timestamp).
  const getDisplayDate = () => {
    if (run.date) {
      const d = run.date;
      if (typeof d === 'string' || typeof d === 'number') return typeof d === 'number' ? new Date(d).toLocaleDateString() : d;
      if (d && (typeof d.toDate === 'function' || typeof d.seconds === 'number')) {
        return (d.toDate ? d.toDate() : new Date(d.seconds * 1000)).toLocaleDateString();
      }
    }

    if (run.createdAt) {
      // Handle Firebase Timestamp object
      if (run.createdAt.toDate && typeof run.createdAt.toDate === 'function') {
        return run.createdAt.toDate().toLocaleDateString();
      }
      // Handle timestamp as number (milliseconds)
      if (typeof run.createdAt === 'number') {
        return new Date(run.createdAt).toLocaleDateString();
      }
      // Handle timestamp object with seconds property
      if (run.createdAt.seconds) {
        return new Date(run.createdAt.seconds * 1000).toLocaleDateString();
      }
      // Try to parse as date string
      return new Date(run.createdAt).toLocaleDateString();
    }
    
    if (run.timestamp) {
      if (typeof run.timestamp === 'number') {
        return new Date(run.timestamp).toLocaleDateString();
      }
      if (run.timestamp.seconds) {
        return new Date(run.timestamp.seconds * 1000).toLocaleDateString();
      }
    }
    
    return 'No date';
  };
  
  const displayDate = getDisplayDate();

  const sourceIcon = useMemo(() => {
    if (isRunPendingOnDevice(run)) {
      return (
        <span
          className="run-card-source-icon run-card-source-icon--sync"
          title="Waiting to sync to the cloud — saved on this device; opens offline until upload completes."
          aria-label="Waiting to sync to the cloud"
        >
          <FaCloudUploadAlt size={13} aria-hidden />
        </span>
      );
    }
    if (!isRunCloudOnly(run)) return null;
    const cachedOnDevice =
      Boolean(run.cachedOnDevice) ||
      (viewerUserId && isRunTelemetryCachedOnDevice(viewerUserId, run.id));
    if (cachedOnDevice) {
      return (
        <span
          className="run-card-source-icon run-card-source-icon--cached"
          title="Synced and cached on this device — full run was opened here; list uses a local flag for this browser."
          aria-label="Synced and cached on this device"
        >
          <FaLaptop size={12} aria-hidden />
        </span>
      );
    }
    return (
      <span
        className="run-card-source-icon run-card-source-icon--cloud"
        title="Stored in the cloud — opening this run needs an internet connection on this device."
        aria-label="Stored in the cloud"
      >
        <FaGlobe size={12} aria-hidden />
      </span>
    );
  }, [run, viewerUserId, cacheTick]);

  const placeholderTint = getAccentPlaceholderTint(run.id);
  const isList = viewMode === 'list';

  return (
    <div
      className={`run-card ${isList ? 'list' : ''} ${selectMode ? 'select-mode' : ''} ${selected ? 'selected' : ''}`}
      onClick={() => {
        if (isDraggingRef.current) return;
        setMenuOpen(false);
        if (selectMode && onToggleSelect) {
          onToggleSelect();
          return;
        }
        onClick?.();
      }}
      draggable
      onDragStart={(e) => {
        try {
          const runId = run?.id || '';
          // Set multiple flavors to improve cross-browser reliability
          e.dataTransfer.setData('text/plain', runId);
          e.dataTransfer.setData('application/x-run-id', runId);
          e.dataTransfer.effectAllowed = 'copy';
          isDraggingRef.current = true;
        } catch (err) {
          // ignore
        }
      }}
      onDragEnd={() => {
        // dragend may fire before click; clear on next tick
        setTimeout(() => {
          isDraggingRef.current = false;
        }, 0);
      }}
    >
      {isList ? (
        <div className="run-card-row">
          <div className="run-card-title">
            {runName}
            {run.pendingSync && (
              <span className="run-card-pending-badge" title="Saved on this device; uploads when you are online">
                Pending sync
              </span>
            )}
            {isMemberRun && <span className="run-card-member-badge" title="Group member's run">Member</span>}
          </div>
          <div className="run-card-row-right">
            <span className="run-card-date-with-icon">
              <span className="run-card-date">{displayDate}</span>
              {sourceIcon}
            </span>
            <div className="run-card-actions">
              {(onOpenGroups || onDelete) && (
                <div className="run-card-menu" ref={menuRef}>
                  <button
                    type="button"
                    title="Menu"
                    className="run-card-action-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuOpen((v) => !v);
                    }}
                  >
                    <FaEllipsisV size={13} />
                  </button>
                  {menuOpen && (
                    <div className="run-card-menu-popover" onClick={(e) => e.stopPropagation()}>
                      {onOpenGroups && (
                        <button
                          type="button"
                          className="run-card-menu-item"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setMenuOpen(false);
                            onOpenGroups();
                          }}
                        >
                          Add to group
                        </button>
                      )}
                      {onDelete && (
                        <button
                          type="button"
                          className="run-card-menu-item danger"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setMenuOpen(false);
                            onDelete();
                          }}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <>
          {hasThumbnail ? (
            <img
              src={`https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`}
              alt="Run thumbnail"
              className="run-card-thumbnail"
            />
          ) : (
            <div className="run-card-placeholder" style={{ background: placeholderTint }}>
              <FaThLarge size={44} />
            </div>
          )}
          <div className="run-card-title">
            {runName}
            {run.pendingSync && (
              <span className="run-card-pending-badge" title="Saved on this device; uploads when you are online">
                Pending sync
              </span>
            )}
            {isMemberRun && <span className="run-card-member-badge" title="Group member's run">Member</span>}
          </div>
          <div className="run-card-footer">
            <span className="run-card-date-with-icon">
              <span className="run-card-date">{displayDate}</span>
              {sourceIcon}
            </span>
            <div className="run-card-actions">
              {(onOpenGroups || onDelete) && (
                <div className="run-card-menu" ref={menuRef}>
                  <button
                    type="button"
                    title="Menu"
                    className="run-card-action-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuOpen((v) => !v);
                    }}
                  >
                    <FaEllipsisV size={13} />
                  </button>
                  {menuOpen && (
                    <div className="run-card-menu-popover" onClick={(e) => e.stopPropagation()}>
                      {onOpenGroups && (
                        <button
                          type="button"
                          className="run-card-menu-item"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setMenuOpen(false);
                            onOpenGroups();
                          }}
                        >
                          Add to group
                        </button>
                      )}
                      {onDelete && (
                        <button
                          type="button"
                          className="run-card-menu-item danger"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setMenuOpen(false);
                            onDelete();
                          }}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}
      {selectMode && (
        <div
          className="run-card-checkmark"
          onClick={(e) => { e.stopPropagation(); onToggleSelect?.(); }}
          role="button"
          tabIndex={0}
          aria-label={selected ? 'Deselect run' : 'Select run'}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggleSelect?.(); } }}
        >
          {selected ? <FaCheckSquare className="checkmark-icon selected" /> : <FaSquare className="checkmark-icon" />}
        </div>
      )}
      {showSelect && !selectMode && (
        <button
          className="run-card-select-btn"
          onClick={e => { e.stopPropagation(); onClick?.(); }}
        >
          Select
        </button>
      )}
    </div>
  );
};

export default RunCard;